import { DateTime } from 'luxon'
import { useState } from 'react'
import type { LogEntry } from '../beacon-run.ts'
import * as styles from './beacon-live-view.css.ts'

/** The newest entries the log keeps on screen; older ones are a scroll the reader never makes. */
const VISIBLE_ENTRIES = 160

export const formatDuration = (milliseconds: number): string => {
  const seconds = Math.max(0, Math.round(milliseconds / 1000))
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}

export type BeaconNarrativeLogProps = {
  entries: readonly LogEntry[]
  startTs?: string
  /** Entries up to this seq were replayed on load and appear without the arrival highlight. */
  settledSeq: number
}

/** "What Beacon is doing": the run's own narration, newest first, filterable to the findings story. */
export const BeaconNarrativeLog: React.FC<BeaconNarrativeLogProps> = ({ entries, startTs, settledSeq }) => {
  const [isFindingsOnly, setIsFindingsOnly] = useState(false)
  const start = startTs ? DateTime.fromISO(startTs).toMillis() : 0
  const shown = entries
    .filter(entry => !isFindingsOnly || entry.isFindingEvent)
    .slice(-VISIBLE_ENTRIES)
    .reverse()

  return (
    <section className={styles.log} aria-label="What Beacon is doing">
      <div className={styles.panelHead}>
        <h2 className={styles.panelTitle}>What Beacon is doing</h2>
        <div className={styles.segmented} role="group" aria-label="Log filter">
          <button
            type="button"
            className={styles.segment}
            aria-pressed={!isFindingsOnly}
            onClick={() => setIsFindingsOnly(false)}
          >
            All
          </button>
          <button
            type="button"
            className={styles.segment}
            aria-pressed={isFindingsOnly}
            onClick={() => setIsFindingsOnly(true)}
          >
            Findings
          </button>
        </div>
      </div>
      <ol className={styles.logList} role="log" aria-label="Scan narrative, newest first" aria-live="polite">
        {shown.length === 0 ? (
          <li className={styles.logItem({ isFresh: false, isStage: false })}>
            <span />
            <span />
            <span className={styles.logMessage}>Waiting for the scan to start.</span>
          </li>
        ) : (
          shown.map(entry => (
            <li
              key={entry.seq}
              className={styles.logItem({ isFresh: entry.seq > settledSeq, isStage: entry.kind === 'stage' })}
            >
              <span className={styles.logTime}>+{formatDuration(DateTime.fromISO(entry.ts).toMillis() - start)}</span>
              <i className={styles.logMark({ kind: entry.kind })} aria-hidden />
              <span className={styles.logMessage}>{entry.message}</span>
            </li>
          ))
        )}
      </ol>
    </section>
  )
}
